import { parse } from "date-fns";
import { parseISO } from "date-fns/parseISO";

import {
	Criteria,
	CriteriaType,
	Cluster
} from "../trajectory/TrajectoryService";

// TODO: this is a temporary quick fix.
export type Filtered = {
	index: number;
	subElements: Filtered[];
};

export type Filter = {
	[key: string]: unknown;
};

export function toFiltered(criterion: Criteria, index: number): Filtered {
	if (criterion.type === CriteriaType.CLUSTER) {
		const cluster = criterion as Cluster;
		return {
			index,
			subElements: cluster.subsections.map((subsection, subIndex) =>
				toFiltered(subsection, subIndex)
			)
		};
	}

	return {
		index,
		subElements: []
	};
}

export function filterCriteria(
	criteria: Criteria[],
	filter: string,
	filterData: Filter
): Filtered[] {
	const lowerCaseFilter = filter.toLowerCase();
	const filtered: Filtered[] = [];

	for (const [index, criterion] of criteria.entries()) {
		const f = matchesLevel1Filter(
			lowerCaseFilter,
			filterData,
			criterion,
			index
		);
		if (f !== null) {
			filtered.push(f);
		}
	}

	return filtered;
}

function checkChildren(
	text: string,
	filter: Filter,
	criterion: Criteria,
	index: number,
	matcher: (
		text: string,
		filter: Filter,
		criterion: Criteria,
		index: number
	) => Filtered | null
): Filtered | null {
	if (criterion.type !== CriteriaType.CLUSTER) {
		return null;
	}

	const cluster = criterion as Cluster;

	const matchedChildren: Filtered[] = [];
	for (const [subIndex, child] of cluster.subsections.entries()) {
		const f = matcher(text, filter, child, subIndex);
		if (f !== null) {
			matchedChildren.push(f);
		}
	}

	if (matchedChildren.length === 0) {
		return null;
	}

	return {
		index,
		subElements: matchedChildren
	};
}

function matchesLevel1Filter(
	text: string,
	filter: Filter,
	criterion: Criteria,
	index: number
): Filtered | null {
	if (matchesGenericFilter(text, filter, criterion)) {
		return toFiltered(criterion, index);
	}

	return checkChildren(text, filter, criterion, index, matchesLevel2Filter);
}

function matchesLevel2Filter(
	text: string,
	filter: Filter,
	criterion: Criteria,
	index: number
): Filtered | null {
	if (matchesGenericFilter(text, filter, criterion)) {
		return toFiltered(criterion, index);
	}

	if (
		(filter?.locationSchool ||
			filter?.locationWork ||
			filter?.locationExternal) &&
		matchesLocation(filter, criterion) === true
	) {
		return toFiltered(criterion, index);
	}

	return checkChildren(text, filter, criterion, index, matchesLevel3Filter);
}

function matchesLevel3Filter(
	text: string,
	filter: Filter,
	criterion: Criteria,
	index: number
): Filtered | null {
	if (matchesGenericFilter(text, filter, criterion)) {
		return toFiltered(criterion, index);
	}

	if (filter?.achieved !== undefined && matchesAchieved(filter, criterion)) {
		return toFiltered(criterion, index);
	}

	if (
		(filter?.locationSchool ||
			filter?.locationWork ||
			filter?.locationExternal) &&
		matchesLocation(filter, criterion) === true
	) {
		return toFiltered(criterion, index);
	}

	if (
		(filter?.scoreMin || filter?.scoreMax) &&
		matchesScore(filter, criterion)
	) {
		return toFiltered(criterion, index);
	}

	if (
		filter?.evaluated !== undefined &&
		matchesEvaluated(filter, criterion)
	) {
		return toFiltered(criterion, index);
	}

	if (
		(filter?.offeredFrom || filter?.offeredUntil) &&
		matchesOfferedDate(filter, criterion)
	) {
		return toFiltered(criterion, index);
	}

	return checkChildren(
		text,
		filter,
		criterion,
		index,
		matchesDeeperLevelFilter
	);
}

function matchesDeeperLevelFilter(
	text: string,
	filter: Filter,
	criterion: Criteria,
	index: number
): Filtered | null {
	if (matchesGenericFilter(text, filter, criterion)) {
		return toFiltered(criterion, index);
	}

	return null;
}

function matchesGenericFilter(
	text: string,
	filter: Filter,
	criterion: Criteria
): boolean {
	let hasFilter = false;
	let matches = true;
	if (text !== "") {
		hasFilter = true;
		matches &&= criterion.name.toLowerCase().includes(text);
	}

	if (filter?.changesFrom || filter?.changesUntil) {
		hasFilter = true;
		matches &&= matchesChangedDate(filter, criterion);
	}

	if (filter?.labels) {
		hasFilter = true;
		const filteredLabel = filter.labels as Array<string>;
		if (filteredLabel.length > 0) {
			const match = criterion.labels.some(label =>
				filteredLabel.includes(label)
			);

			matches &&= match;
		}
	}

	return hasFilter && matches;
}

function matchesChangedDate(filter: Filter, criterion: Criteria): boolean {
	const from = filter.changesFrom as Date;
	const until = filter.changesUntil as Date;

	const key = Object.keys(criterion.latestProperties || {})[0];
	if (!key) {
		return false;
	}
	const date = parseISO(key);
	return (from ? date >= from : true) && (until ? date <= until : true);
}

function matchesOfferedDate(filter: Filter, criterion: Criteria): boolean {
	const from = filter.offeredFrom as Date;
	const until = filter.offeredUntil as Date;

	const value = Object.values(criterion.latestProperties || {})[0]?.date;
	if (!value) {
		return false;
	}

	const date = parse(value, "dd/MM/yyyy", new Date());
	return (from ? date >= from : true) && (until ? date <= until : true);
}

function matchesLocation(filter: Filter, criterion: Criteria): boolean {
	const school = filter.locationSchool as boolean;
	const work = filter.locationWork as boolean;
	const external = filter.locationExternal as boolean;

	const value = Object.values(criterion.latestProperties || {})[0]?.location;
	if (!value) {
		return false;
	}

	return (
		(school ? value.includes("school") : true) &&
		(work ? value.includes("werkplek") : true) &&
		(external ? value.includes("extern") : true)
	);
}

function matchesAchieved(filter: Filter, criterion: Criteria): boolean {
	const achieved = filter.achieved as boolean;

	const value = Object.values(criterion.latestProperties || {})[0]?.achieved;

	if (achieved === true) {
		return value === "true";
	} else if (achieved === false) {
		return value === undefined || value === "false";
	}

	return true;
}

function matchesScore(filter: Filter, criterion: Criteria): boolean {
	const min = parseInt(filter.scoreMin as string);
	const max = parseInt(filter.scoreMax as string);

	const value = Object.values(criterion.latestProperties || {})[0]?.score;
	if (!value) {
		return false;
	}

	const score = parseInt(value);
	return (min ? score >= min : true) && (max ? score <= max : true);
}

function matchesEvaluated(filter: Filter, criterion: Criteria): boolean {
	const evaluated = filter.evaluated as boolean;

	if (evaluated === true) {
		return criterion.latestProperties !== null;
	} else if (evaluated === false) {
		return criterion.latestProperties === null;
	}

	return true;
}
